import { useMutation } from '@apollo/client/react';
import { GET_CONTACT_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { DELETE_EMAILS_MUTATION } from '../../integrations/allkort/graphql/gql/EmailMutations';
import { DeleteEmailsResponse } from '../../integrations/allkort/graphql/interface/Emails';

/**
 * useDeleteEmail
 *
 * Custom hook for deleting an email address from the customer's account.
 *
 * @example
 * ```tsx
 * const { deleteEmail, loading, error } = useDeleteEmail();
 *
 * const handleDelete = async () => {
 *   const result = await deleteEmail({ emailId: 382339 });
 *   if (result) console.log('Deleted email IDs:', result.emailIds);
 * };
 * ```
 */
export const useDeleteEmail = () => {
	const [deleteEmailsMutation, { loading, error, data }] = useMutation<DeleteEmailsResponse>(
		DELETE_EMAILS_MUTATION
	);

	/**
	 * Delete an email address from the customer's account
	 * @param emailId - The ID of the email to delete
	 * @returns The emailDeletePayload with deleted emailIds, or null if the mutation failed
	 * @throws Error with the API message if the mutation fails
	 */
	const deleteEmail = async ({ emailId }: { emailId: number }) => {
		try {
			const result = await deleteEmailsMutation({
				variables: { emailIds: [emailId] },
				refetchQueries: [{ query: GET_CONTACT_CUSTOMER_DATA_QUERY }],
				awaitRefetchQueries: true,
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useDeleteEmail] GraphQL error:', message);
				throw new Error(message);
			}

			const payload = result.data?.deleteEmails?.emailDeletePayload ?? null;
			console.log('[useDeleteEmail] Deleted successfully:', payload);
			return payload;
		} catch (err) {
			throw err;
		}
	};

	return { deleteEmail, loading, error, data };
};
